/**
 * Itemized employer payroll tax estimates for the Finances loaded-cost model.
 * Uses static new-employer SUTA rates — not experience-rated accounts.
 */
import { parseLocationToAddress } from './locationFormatter';

/** IRS Social Security wage base (2026). */
export const SS_WAGE_BASE_ANNUAL = 176_100;
export const SS_EMPLOYER_RATE = 0.062;
export const MEDICARE_EMPLOYER_RATE = 0.0145;
export const FUTA_WAGE_BASE_ANNUAL = 7_000;
/** Effective FUTA after standard 5.4% state credit. */
export const FUTA_EFFECTIVE_RATE = 0.006;

export const DEFAULT_WORK_STATE = 'VA';

export interface EmployerTaxBreakdown {
  socialSecurity: number;
  medicare: number;
  futa: number;
  suta: number;
  state: string;
}

export const ZERO_EMPLOYER_TAX_BREAKDOWN: EmployerTaxBreakdown = {
  socialSecurity: 0,
  medicare: 0,
  futa: 0,
  suta: 0,
  state: DEFAULT_WORK_STATE,
};

/** New-employer SUTA rate + annual taxable wage base per state. */
const SUTA_BY_STATE: Record<string, { rate: number; wageBase: number }> = {
  AL: { rate: 0.027, wageBase: 8_000 },
  AK: { rate: 0.027, wageBase: 47_100 },
  AZ: { rate: 0.02, wageBase: 8_000 },
  AR: { rate: 0.03, wageBase: 7_000 },
  CA: { rate: 0.034, wageBase: 7_000 },
  CO: { rate: 0.017, wageBase: 23_800 },
  CT: { rate: 0.027, wageBase: 15_000 },
  DE: { rate: 0.015, wageBase: 10_500 },
  DC: { rate: 0.027, wageBase: 9_000 },
  FL: { rate: 0.027, wageBase: 7_000 },
  GA: { rate: 0.027, wageBase: 9_500 },
  HI: { rate: 0.03, wageBase: 59_100 },
  ID: { rate: 0.01, wageBase: 53_200 },
  IL: { rate: 0.035, wageBase: 13_590 },
  IN: { rate: 0.025, wageBase: 9_500 },
  IA: { rate: 0.01, wageBase: 38_200 },
  KS: { rate: 0.027, wageBase: 14_000 },
  KY: { rate: 0.027, wageBase: 11_100 },
  LA: { rate: 0.027, wageBase: 7_700 },
  ME: { rate: 0.027, wageBase: 12_000 },
  MD: { rate: 0.027, wageBase: 8_500 },
  MA: { rate: 0.024, wageBase: 15_000 },
  MI: { rate: 0.027, wageBase: 9_500 },
  MN: { rate: 0.01, wageBase: 43_000 },
  MS: { rate: 0.01, wageBase: 14_000 },
  MO: { rate: 0.027, wageBase: 10_500 },
  MT: { rate: 0.027, wageBase: 43_000 },
  NE: { rate: 0.027, wageBase: 9_000 },
  NV: { rate: 0.027, wageBase: 41_500 },
  NH: { rate: 0.027, wageBase: 14_000 },
  NJ: { rate: 0.027, wageBase: 43_300 },
  NM: { rate: 0.01, wageBase: 31_700 },
  NY: { rate: 0.041, wageBase: 12_500 },
  NC: { rate: 0.01, wageBase: 31_400 },
  ND: { rate: 0.01, wageBase: 43_500 },
  OH: { rate: 0.027, wageBase: 9_000 },
  OK: { rate: 0.015, wageBase: 27_000 },
  OR: { rate: 0.021, wageBase: 52_800 },
  PA: { rate: 0.036, wageBase: 10_000 },
  RI: { rate: 0.027, wageBase: 29_200 },
  SC: { rate: 0.027, wageBase: 14_000 },
  SD: { rate: 0.012, wageBase: 15_000 },
  TN: { rate: 0.027, wageBase: 7_000 },
  TX: { rate: 0.027, wageBase: 9_000 },
  UT: { rate: 0.027, wageBase: 47_000 },
  VT: { rate: 0.01, wageBase: 14_100 },
  VA: { rate: 0.025, wageBase: 14_000 },
  WA: { rate: 0.027, wageBase: 68_500 },
  WV: { rate: 0.027, wageBase: 9_000 },
  WI: { rate: 0.03, wageBase: 14_000 },
  WY: { rate: 0.027, wageBase: 30_900 },
};

const DEFAULT_SUTA = { rate: 0.027, wageBase: 12_000 };

export const US_STATE_CODES = Object.keys(SUTA_BY_STATE).sort();

export function normalizeWorkState(state: string | null | undefined): string {
  const s = (state || '').trim().toUpperCase();
  if (s.length === 2 && SUTA_BY_STATE[s]) return s;
  if (s.length === 2) return s;
  return DEFAULT_WORK_STATE;
}

export function resolveWorkState(
  positionWorkState: string | null | undefined,
  crmUserLocation: string | null | undefined,
  defaultWorkState: string = DEFAULT_WORK_STATE,
): string {
  if (positionWorkState?.trim()) {
    return normalizeWorkState(positionWorkState);
  }
  if (crmUserLocation?.trim()) {
    const parsed = parseLocationToAddress(crmUserLocation);
    if (parsed.state) return normalizeWorkState(parsed.state);
  }
  return normalizeWorkState(defaultWorkState) || DEFAULT_WORK_STATE;
}

function cappedMonthlyTax(annualTaxable: number, wageBase: number, rate: number): number {
  const cappedAnnual = Math.min(Math.max(0, annualTaxable), wageBase);
  return (cappedAnnual * rate) / 12;
}

export interface ComputeEmployerTaxInput {
  monthlyTaxable: number;
  workState: string;
  /** When set, returns a single flat employer-tax line (legacy override). */
  flatOverrideRate?: number | null;
}

export interface ComputeEmployerTaxResult {
  breakdown: EmployerTaxBreakdown;
  total: number;
  isFlatOverride: boolean;
}

export function computeEmployerPayrollTaxes(input: ComputeEmployerTaxInput): ComputeEmployerTaxResult {
  const { monthlyTaxable, flatOverrideRate } = input;
  const state = normalizeWorkState(input.workState);
  const taxable = Math.max(0, monthlyTaxable);
  const annualTaxable = taxable * 12;

  if (flatOverrideRate != null) {
    return {
      breakdown: { ...ZERO_EMPLOYER_TAX_BREAKDOWN, state },
      total: taxable * flatOverrideRate,
      isFlatOverride: true,
    };
  }

  const ssMonthlyCap = SS_WAGE_BASE_ANNUAL / 12;
  const socialSecurity = Math.min(taxable, ssMonthlyCap) * SS_EMPLOYER_RATE;
  const medicare = taxable * MEDICARE_EMPLOYER_RATE;
  const futa = cappedMonthlyTax(annualTaxable, FUTA_WAGE_BASE_ANNUAL, FUTA_EFFECTIVE_RATE);

  const sutaConfig = SUTA_BY_STATE[state] ?? DEFAULT_SUTA;
  const suta = cappedMonthlyTax(annualTaxable, sutaConfig.wageBase, sutaConfig.rate);

  const breakdown: EmployerTaxBreakdown = {
    socialSecurity,
    medicare,
    futa,
    suta,
    state,
  };

  return {
    breakdown,
    total: socialSecurity + medicare + futa + suta,
    isFlatOverride: false,
  };
}

export function getSutaRateForState(state: string): number {
  const normalized = normalizeWorkState(state);
  return (SUTA_BY_STATE[normalized] ?? DEFAULT_SUTA).rate;
}

function remainingWageBase(alreadyPaid: number, wageBase: number): number {
  return Math.max(0, wageBase - Math.max(0, alreadyPaid));
}

export interface ComputeSupplementalTaxInput {
  supplementalWages: number;
  /** Annual W-2 wages already counted toward FICA / FUTA / SUTA wage bases. */
  annualWagesAlreadyPaid: number;
  workState: string;
  flatOverrideRate?: number | null;
}

/**
 * Employer tax on a one-time bonus / supplemental wage, honoring annual
 * wage-base remaining after regular pay.
 */
export function computeEmployerTaxOnSupplementalWages(
  input: ComputeSupplementalTaxInput,
): ComputeEmployerTaxResult {
  const state = normalizeWorkState(input.workState);
  const bonus = Math.max(0, input.supplementalWages);
  const alreadyPaid = Math.max(0, input.annualWagesAlreadyPaid);

  if (input.flatOverrideRate != null) {
    return {
      breakdown: { ...ZERO_EMPLOYER_TAX_BREAKDOWN, state },
      total: bonus * input.flatOverrideRate,
      isFlatOverride: true,
    };
  }

  const socialSecurity = Math.min(bonus, remainingWageBase(alreadyPaid, SS_WAGE_BASE_ANNUAL)) * SS_EMPLOYER_RATE;
  const medicare = bonus * MEDICARE_EMPLOYER_RATE;
  const futa = Math.min(bonus, remainingWageBase(alreadyPaid, FUTA_WAGE_BASE_ANNUAL)) * FUTA_EFFECTIVE_RATE;
  const sutaConfig = SUTA_BY_STATE[state] ?? DEFAULT_SUTA;
  const suta = Math.min(bonus, remainingWageBase(alreadyPaid, sutaConfig.wageBase)) * sutaConfig.rate;

  const breakdown: EmployerTaxBreakdown = {
    socialSecurity,
    medicare,
    futa,
    suta,
    state,
  };

  return {
    breakdown,
    total: socialSecurity + medicare + futa + suta,
    isFlatOverride: false,
  };
}
